import "../index.scss";

import * as React from "react";

import { AuthLayout } from "@components/account/AuthLayout";
import InitResetPassword from "@components/account/InitResetPassword";
import LoginForm from "@components/account/LoginForm";
import ResetPassord from "@components/account/ResetPasswordForm";
import SignupForm from "@components/account/SignupForm";
import SignUpLandingPage from "@components/account/SignupFormLandingPage";
import { FullScreenLoader } from "@components/common/FullScreenLoader";
import SentryBoundary from "@components/common/SentryBoundary";
import { PartnerProgramLPContent } from "@components/dashboard/partner/partner-program-v2-lp-components/PartnerProgramLP";
import { PartnershipReferralBoard } from "@components/dashboard/partner/PartnershipReferralBoard";
import AllProjects from "@components/dashboard/projects/AllProjects";
import AuthenticatedUserRequiredRoute from "@components/router/AuthenticatedUserRequiredRoute";
import ReferralCodeRedirectRoute from "@components/router/ReferralCodeRedirectRoute";
import ShopifyInstalledOnExistingWorkspaceLayout from "@editor/components/layouts/integrations/shopify/ShopifyInstalledOnExistingWorkspaceLayout";
import ShopifyInstalledOnHostingBilledThroughShopifyLayout from "@editor/components/layouts/integrations/shopify/ShopifyInstalledOnHostingBilledThroughShopifyLayout";
import ShopifyInstalledOnNoAccessWorkspaceLayout from "@editor/components/layouts/integrations/shopify/ShopifyInstalledOnNoAccessWorkspaceLayout";
import ShopifyPendingAppInstallationLayout from "@editor/components/layouts/integrations/shopify/ShopifyPendingAppInstallationLayout";
import WorkspaceWrappedOnboarding from "@editor/components/onboarding/WorkspaceWrappedOnboarding";
import DashboardLayout from "@editor/components/shops-ui/DashboardLayout";
import { DesignLibrary } from "@editor/components/shops-ui/designLibrary/DesignLibrary";
import Home from "@editor/components/shops-ui/home/Home";
import FontManager from "@editor/components/shops-ui/settings/shop/FontManager";
import Integrations from "@editor/components/shops-ui/settings/shop/Integrations";
import IntegrationsEdit from "@editor/components/shops-ui/settings/shop/integrations/IntegrationsEdit";
import IntegrationsHome from "@editor/components/shops-ui/settings/shop/integrations/IntegrationsHome";
import ScriptManager from "@editor/components/shops-ui/settings/shop/ScriptManager";
import ShopSettings from "@editor/components/shops-ui/settings/ShopSettings";
import WorkspaceMembers from "@editor/components/shops-ui/settings/workspace/Members";
import Pages from "@editor/components/shops-ui/site/Pages";
import { StyleExtraction } from "@editor/components/shops-ui/StyleExtraction";
import { CurrentChatProvider } from "@editor/contexts/CurrentChatContext";
import CurrentProjectProvider, {
  useCurrentProjectContext,
} from "@editor/contexts/CurrentProjectContext";
import { CurrentUserProvider } from "@editor/contexts/CurrentUserContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { store } from "@editor/store";
import { marketingRouter } from "@editor/trpc/marketing";
import { workspaceRouter } from "@editor/trpc/workspace";
import { TRPCProviders } from "@editor/utils/trpcProviders";
import { LocalStorageProvider } from "@hooks/useLocalStorage";
import { AIStreamingProvider } from "@providers/AIStreamingProvider";
import { addReferralToUrl, routes } from "@utils/router";

import Profile from "@/editor/components/shops-ui/Profile";
import AbTestingSettings from "@/editor/components/shops-ui/settings/shop/AbTesting";
import AddDomainPage from "@/editor/components/shops-ui/settings/shop/AddDomainPage";
import ShopGeneralSettings from "@/editor/components/shops-ui/settings/shop/General";
import WorkspaceSettings from "@/editor/components/shops-ui/settings/workspace/General";
import { ShopsRedirectRoute } from "@/editor/components/shops-ui/ShopsRedirectRoute";
import { AnalyticsHome } from "@/features/analytics/AnalyticsHome";
import { AnalyticsOverview } from "@/features/analytics/AnalyticsOverview";
import { AnalyticsRedirect } from "@/features/analytics/AnalyticsRedirect";
import AnalyticsInsights from "@/features/analytics/insights/AnalyticsInsights";
import { InsightsRedirect } from "@/features/analytics/insights/InsightsRedirect";
import PageDetails from "@/features/analytics/PageDetails";
import { ProjectAnalyticsDashboard } from "@/features/analytics/WorkspaceAnalyticsDashboard";
import { NextDevServerProvider } from "@/features/code-execution/context";
import ExperimentEditWrapper from "@/features/experiments/ExperimentEditWrapper";
import Experiments from "@/features/experiments/Experiments";
import { ExperimentsRedirect } from "@/features/experiments/ExperimentsRedirect";
import ExperimentDetailsTabV2 from "@/features/experiments/tabs/ExperimentDetailsTab";
import ExperimentResultsTab from "@/features/experiments/tabs/ExperimentResultsTab";
import { ProductEditTab } from "@/features/products/ProductEditTab";
import { ProductEditWrapper } from "@/features/products/ProductEditWrapper";
import { ProductsDashboard } from "@/features/products/ProductsDashboard";
import { ChatProvider } from "@/features/react-editor/components/chat/store/ChatStoreContext";
import ChatLayout from "@/features/react-editor/layouts/ChatLayout";
import ReactEditor from "@/features/react-editor/ReactEditor";
import Billing from "@/features/workspace/Billing";
import { WorkspaceBillingRedirectRoute } from "@/features/workspace/WorkspaceBillingRedirectRoute";
import WorkspaceDashboard from "@/features/workspace/WorkspaceDashboard";
import { ToastManager } from "@replo/design-system/components/alert/Toast";
import { CookiesProvider } from "react-cookie";
import { Provider as ReduxProvider } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

import { SandboxStateProvider } from "../../features/react-editor/store/SandboxStateStoreContext";
import AppModals from "../components/AppModals";
import { BrandDetailsProvider } from "../components/designLibrary/BrandDetailsContext";
import ShareTemplateLayout from "../components/layouts/ShareTemplateLayout";
import ShopDesignLibrary from "../components/shops-ui/newShopOnboarding/ShopDesignLibrary";
import { useShopCreationFlowRedirect } from "../components/shops-ui/newShopOnboarding/useShopCreationFlowRedirect";
import {
  useCurrentWorkspaceContext,
  useCurrentWorkspaceId,
} from "../contexts/WorkspaceDashboardContext";
import { useIsProjectSetup } from "../hooks/useIsProjectSetup";
import { useModal } from "../hooks/useModal";
import ElementEditorLayout from "./ElementEditorLayout";
import TemplateLibraryEditorLayout from "./TemplateLibraryEditorLayout";

/**
 * Paths in onboarding that the user should ONLY see if they have exactly ZERO projects
 * they can access on their account (i.e., they just started and have no projects yet,
 * and have not been invited to one)
 */
const onboardingZeroProjectOnlyPaths = [
  routes.onboarding.setupShop,
  routes.onboarding.shopInfo,
];

/**
 * Whether a given url path is part of onboarding
 */
const isOnboardingPath = (pathname: string) => {
  return pathname.startsWith("/onboarding");
};

/**
 * Component which redirects away from specific routes based on the current project type.
 * I.e. if you access /chat on a v1 project we want to redirect them back to the site pages
 * at /site. The /chat should be wrapped in this component with the specific type in this case.
 */
const RedirectBasedOnProjectType: React.FC<
  React.PropsWithChildren<{
    type:
      | "v2Home"
      | "v1SitePages"
      | "v2Chat"
      | "v2DesignLibrary"
      | "v2StyleExtraction";
  }>
> = ({ children, type }) => {
  const { project } = useCurrentProjectContext();

  if (!project) {
    return <>{children}</>;
  }

  if (project.hostingType === "replo" && type === "v1SitePages") {
    // Note (Noah, 2025-09-18): If they're trying to access v1 site pages tab but we're a v2 projet,
    // we'll redirect them to v2 home
    return (
      <Navigate
        to={generatePath(routes.shops.home, { projectId: project.id })}
        replace
      />
    );
  }

  if (project.hostingType !== "replo" && ["v2Home", "v2Chat"].includes(type)) {
    // Note (Noah, 2025-09-18): If they're trying to access v2 home or chat but we're a v1 project,
    // we'll redirect them to v1 site pages
    return (
      <Navigate
        to={generatePath(routes.shops.site, { projectId: project.id })}
        replace
      />
    );
  }

  return <>{children}</>;
};

export function App() {
  return (
    <SentryBoundary>
      <ReduxProvider store={store}>
        <TRPCProviders>
          <RouterProvider router={router} />
        </TRPCProviders>
      </ReduxProvider>
    </SentryBoundary>
  );
}

// NOTE (Gabe 2025-07-24): Cross-Origin (COOP/COEP) headers are required for
// WebContainers to operate. These headers break oauth flows and vimeo embeds.
// Our vercel.json + vite.config.mts are configured to serve the correct headers
// based on pathname, but they work only on direct navigations that are served
// from the server. Clientside navigations do not involve hitting the server and
// obtaining new headers. For that reason, we need to manually check the
// crossOriginIsolated flag for the page on each navigation and if it's not
// consistent with what it should be for the pathname, we need to trigger server
// loaded navigation.

/**
 * Determines if a given pathname requires WebContainer support, which needs
 * cross-origin isolation (COOP/COEP) headers. These headers are required for
 * WebContainers to operate, but they break OAuth flows and Vimeo embeds.
 */
function requiresWebContainerSupport(pathname: string): boolean {
  return (
    !isFeatureEnabled("use-server-sandbox") &&
    !pathname.startsWith("/auth") &&
    !pathname.startsWith("/editor") &&
    !pathname.endsWith("/settings/shop/add-domain")
  );
}

function triggerServerLoadedNavigationIfNeeded(urlString: string) {
  // NOTE (Gabe 2025-08-06): Early exit for Safari - WebContainers don't work
  // in the same way on Safari, and the COEP header we use (credentialless) is
  // not supported on Safari. This results in window.crossOriginIsolated
  // being false all the time, and a reload loop without this check.
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    return;
  }
  const url = new URL(urlString);

  const doesRequireWebContainerSupport = requiresWebContainerSupport(
    url.pathname,
  );
  if (doesRequireWebContainerSupport !== window.crossOriginIsolated) {
    if (doesRequireWebContainerSupport) {
      url.searchParams.set("webcontainer", "true");
    } else {
      url.searchParams.delete("webcontainer");
    }
    window.location.replace(url);
  }
}

const router = (() => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={<RootRoute />}
        loader={({ request }) => {
          triggerServerLoadedNavigationIfNeeded(request.url);
          return null;
        }}
        // NOTE (Gabe 2025-07-24): We need to revalidate the route on every
        // navigation to ensure that the correct headers are set.
        shouldRevalidate={() => true}
      >
        {/* Index redirect route */}
        <Route index element={<RootRouteIndexRedirectRoute />} />
        {/* Auth routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route index element={<Navigate to={routes.signup} replace />} />
          <Route path="login" element={<LoginForm />} />
          <Route path="signup" element={<SignupForm />} />
          <Route path="password/reset/init" element={<InitResetPassword />} />
          <Route path="password/reset" element={<ResetPassord />} />
        </Route>
        {/* Signup Landing Page Route using its own layout, but under /auth path */}
        <Route path="/auth/signup-2" element={<SignUpLandingPage />} />
        {/* Redirect routes */}
        <Route
          path="/signup/:referralCode"
          element={<ReferralCodeRedirectRoute />}
        />
        <Route
          path="/partner/program"
          element={<PartnerProgramRedirectRoute />}
        />
        <Route
          path="/workspace/billing"
          element={<WorkspaceBillingRedirectRoute />}
        />
        <Route element={<ShopsRedirectRoute />}>
          <Route path={routes.home.root} element={<ShopsRedirectRoute />} />
          <Route
            path={routes.home.shareTemplate}
            element={<ShopsRedirectRoute />}
          />

          {/* Project routes */}
          <Route
            path={routes.allProjects}
            element={
              <AuthenticatedUserRequiredRoute>
                <AllProjects>
                  <AppModals />
                </AllProjects>
              </AuthenticatedUserRequiredRoute>
            }
          />

          {/* Workspace routes */}
          <Route
            path="/workspace/:workspaceId"
            element={
              <AuthenticatedUserRequiredRoute>
                <WorkspaceDashboard />
              </AuthenticatedUserRequiredRoute>
            }
          >
            <Route index element={<WorkspaceIndexRedirectRoute />} />
            <Route path="settings" element={<ShopSettings />}>
              <Route index element={<Navigate to="general" replace />} />
              <Route path="general" element={<WorkspaceSettings />} />
              <Route path="members" element={<WorkspaceMembers />} />
              <Route path="billing" element={<Billing />} />
              <Route path="affiliates">
                <Route index element={<Navigate to="home" replace />} />
                <Route path="program" element={<PartnerProgramLPContent />} />
                <Route
                  path="home"
                  element={
                    <ActivePartnershipRequiredRoute>
                      <PartnershipReferralBoard />
                    </ActivePartnershipRequiredRoute>
                  }
                />
              </Route>
            </Route>
          </Route>
          {/* Analytics workspace agnostic route */}
          <Route path="/analytics" element={<AnalyticsRedirect />} />
          {/* Experiments workspace agnostic route */}
          <Route path="/abtesting" element={<ExperimentsRedirect />} />
          {/* Insights workspace agnostic route */}
          <Route path="/analytics/insights" element={<InsightsRedirect />} />
          {/* Editor routes */}
          <Route path="/editor/:projectId" element={<ProjectRouteWrapper />}>
            <Route
              path="new"
              element={
                <TemplateLibraryEditorLayout>
                  <AppModals />
                </TemplateLibraryEditorLayout>
              }
            />
            <Route
              path=":elementId"
              element={
                <ElementEditorLayout>
                  <AppModals />
                </ElementEditorLayout>
              }
            />
          </Route>

          {/* Onboarding routes */}
          <Route path="/onboarding">
            <Route
              index
              element={<Navigate to="/onboarding/setup" replace />}
            />
            <Route
              path=":onboardingStep"
              element={
                <AuthenticatedUserRequiredRoute>
                  <WorkspaceWrappedOnboarding>
                    <AppModals />
                  </WorkspaceWrappedOnboarding>
                </AuthenticatedUserRequiredRoute>
              }
            />
          </Route>
        </Route>
        <Route
          path="/template/:templateId"
          element={
            <AuthenticatedUserRequiredRoute>
              <ShareTemplateLayout />
            </AuthenticatedUserRequiredRoute>
          }
        />
        {/* Shops routes */}
        <Route
          path="/shops/:projectId"
          element={
            <AuthenticatedUserRequiredRoute>
              <ToastManager />
              <ProjectRouteWrapper navigateToDefaultElementAfterFetch={false} />
            </AuthenticatedUserRequiredRoute>
          }
        >
          <Route index element={<Navigate to="site" replace />} />

          {/* Shops with dashboard layout */}
          <Route element={<DashboardLayout contentIsCard={true} />}>
            <Route path="settings/profile" element={<Profile />} />
            {/* /site */}
            <Route
              path="site"
              element={
                <RedirectBasedOnProjectType type="v1SitePages">
                  <Pages />
                </RedirectBasedOnProjectType>
              }
            />

            <Route path="home">
              <Route
                index
                element={
                  <RedirectBasedOnProjectType type="v2Home">
                    <Home />
                  </RedirectBasedOnProjectType>
                }
              />
            </Route>

            {/* /analytics */}
            <Route path="analytics" element={<ProjectAnalyticsDashboard />}>
              <Route path="home" element={<AnalyticsHome />} />
              <Route path="overview" element={<AnalyticsOverview />} />
              <Route path="deep-dive" element={<PageDetails />} />
              <Route path="insights" element={<AnalyticsInsights />} />
            </Route>

            {/* /abtesting */}
            <Route path="abtesting" element={<Experiments />} />
            <Route
              path="abtesting/:experimentId"
              element={<ExperimentEditWrapper />}
            >
              <Route
                path={routes.shops.experiments.details}
                element={<ExperimentDetailsTabV2 />}
              />
              <Route
                path={routes.shops.experiments.results}
                element={<ExperimentResultsTab />}
              />
            </Route>

            {/* /design-library */}
            <Route
              path="design-library"
              element={
                <RedirectBasedOnProjectType type="v2DesignLibrary">
                  <DesignLibrary />
                </RedirectBasedOnProjectType>
              }
            />

            {/* /style-extraction */}
            <Route
              path="style-extraction"
              element={
                <RedirectBasedOnProjectType type="v2StyleExtraction">
                  <StyleExtraction />
                </RedirectBasedOnProjectType>
              }
            />

            {/* /products */}
            <Route path="products" element={<ProductsDashboard />} />

            {/* /settings */}
            <Route path="settings">
              <Route index element={<Navigate to="shop/general" replace />} />
              {/* /settings/shop */}
              <Route path="shop" element={<ShopSettings />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<ShopGeneralSettings />} />
                <Route path="add-domain" element={<AddDomainPage />} />
                <Route path="fonts" element={<FontManager />} />
                <Route path="abtesting" element={<AbTestingSettings />} />
                <Route path="scripts" element={<ScriptManager />} />
                <Route path="integrations" element={<Integrations />}>
                  <Route index element={<Navigate to="home" replace />} />
                  <Route path="home" element={<IntegrationsHome />} />
                  <Route
                    path=":integrationId/edit"
                    element={<IntegrationsEdit />}
                  />
                </Route>
              </Route>
              {/* /settings/workspace */}
              <Route path="workspace" element={<ShopSettings />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<WorkspaceSettings />} />
                <Route path="members" element={<WorkspaceMembers />} />
                <Route path="billing" element={<Billing />} />
                <Route path="affiliates">
                  <Route index element={<Navigate to="home" replace />} />
                  <Route path="program" element={<PartnerProgramLPContent />} />
                  <Route
                    path="home"
                    element={
                      <ActivePartnershipRequiredRoute>
                        <PartnershipReferralBoard />
                      </ActivePartnershipRequiredRoute>
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route element={<DashboardLayout contentIsCard={false} />}>
            <Route element={<ChatLayout />}>
              <Route
                path="chat"
                element={
                  <RedirectBasedOnProjectType type="v2Chat">
                    <ReactEditor />
                  </RedirectBasedOnProjectType>
                }
              />
            </Route>
            <Route element={<ProductEditWrapper />}>
              {
                // NOTE (Kurt, 2025-09-01): We put this products edit route in the
                // contentIsCard = false layout because we want the product edits screen
                // to have two seperate cards instead of a unified one.
              }
              <Route
                path="products/:productId/edit"
                element={<ProductEditTab />}
              />
              <Route path="products/new" element={<ProductEditTab />} />
            </Route>
          </Route>
        </Route>

        {/* Integrations routes */}
        <Route path="shopify">
          <Route
            path="install"
            element={<ShopifyPendingAppInstallationLayout />}
          />
          <Route path="error">
            <Route
              path="different-workspace"
              element={<ShopifyInstalledOnExistingWorkspaceLayout />}
            />
            <Route
              path="no-access-workspace"
              element={<ShopifyInstalledOnNoAccessWorkspaceLayout />}
            />
            <Route
              path="hosting-billed-through-shopify"
              element={<ShopifyInstalledOnHostingBilledThroughShopifyLayout />}
            />
          </Route>
        </Route>

        {/*  404 redirect route */}
        <Route path="*" element={<Navigate to={routes.home.root} replace />} />
      </Route>,
    ),
  );
})();

function RootRoute() {
  return (
    <RootRouteProviders>
      <RootRouteLayout>
        <Outlet />
      </RootRouteLayout>
    </RootRouteProviders>
  );
}

function RootRouteProviders({ children }: React.PropsWithChildren) {
  return (
    // NOTE (Martin, 2024-10-09): We need an extra SentryBoundary component
    // here so that Sentry can catch errors inside the routing tree before
    // the React Router Boundary does it.
    <SentryBoundary>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
        <LocalStorageProvider>
          <CurrentUserProvider>
            <AIStreamingProvider>
              <BrandDetailsProvider>
                <ChatProvider>{children}</ChatProvider>
              </BrandDetailsProvider>
            </AIStreamingProvider>
          </CurrentUserProvider>
        </LocalStorageProvider>
      </CookiesProvider>
    </SentryBoundary>
  );
}

/**
 * This is the standard Google Tag Manager snippet that's usually in the
 * main index.html for an SPA. We don't need GTM to load on all pages,
 * however, so this uses the location to check if it contains the `/auth`
 * prefix for sign ups + logins
 *
 * @author Ben Vogt 2025-05-02
 */
function useGoogleTagManager() {
  const location = useLocation();

  React.useEffect(() => {
    const isSignUpPath =
      location.pathname.includes("/auth") ||
      location.pathname.includes("/onboarding");

    if (!isSignUpPath || process.env.NODE_ENV !== "production") {
      return;
    }

    const scriptHTML = `
      <!-- Google Tag Manager -->
      <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-MH56PQ8');</script>
      <!-- End Google Tag Manager -->
    `;

    const tempElement = document.createElement("div");
    tempElement.innerHTML = scriptHTML.trim();
    if (tempElement.firstChild) {
      document.head.append(tempElement.firstChild);
    }
  }, [location.pathname]);
}

function RootRouteLayout({ children }: React.PropsWithChildren) {
  const { closeModalsWithExclusions } = useModal();

  const referral = useReferralFromSearchParams();
  const { pathname } = useLocation();
  const { user, isLoading: isLoadingUser, isAuthenticated } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const hash = window.location.hash;
  const seedToken = hash.startsWith("#seed=")
    ? decodeURIComponent(hash.slice(6))
    : null;

  const { isLoading: isLoadingMarketingSeed } =
    marketingRouter.getMarketingWebsiteSeedData.useQuery(
      { seed: seedToken! },
      {
        enabled: !!seedToken,
        select: (seedData) => {
          if (seedData) {
            localStorage.setItem(
              "replo_marketing_seed",
              JSON.stringify({
                prompt: seedData.prompt,
                jti: seedData.jti,
                hasImage: Boolean(seedData.imageUrl),
              }),
            );
            window.history.replaceState(
              null,
              "",
              window.location.pathname + window.location.search,
            );
          }
          return seedData;
        },
      },
    );

  useShopCreationFlowRedirect();

  useOnValueChange(pathname, () => {
    closeModalsWithExclusions({
      typesToExclude: [
        "deleteSwatchConfirmationModal",
        "deleteElementConfirmationModal",
      ],
    });
  });

  useGoogleTagManager();

  if (isLoadingUser || isLoadingMarketingSeed) {
    return <FullScreenLoader />;
  }

  // NOTE (Martin, 2024-10-03): if we are not in auth routes and the user is not
  // authenticated, we redirect to the signup page.
  if (!pathname.startsWith("/auth") && !isLoadingUser && !isAuthenticated) {
    return <Navigate to={addReferralToUrl(routes.signup, referral)} replace />;
  }

  // NOTE (Ben O. 2025-04-28): If the user hasn't completed onboarding, we redirect to the onboarding page.
  // this doesn't apply if we are redirecting after a Shopify integration from onboarding. useConnectShopifyIntegration
  // handles the redirection for that.

  // NOTE (Berkley, 2025-09-10): This is the state check for whether the user has completed onboarding
  // First with check if the user is associated with a project or workspace with a project. Basically, is there a project they can go to?
  // If so, we do not redirect, and let them continue to their route
  // If not, we check if the user has a first and last name. If not, we direct them to user setup step
  // If they do, we direct them to the project setup step.
  const userHasAccessToAnyProject = Boolean(user?.defaultProjectId);

  // Note (Noah, 2025-09-26): If the user has access to any project and this happens to be a
  // route that we only want them to see if they have no projects, then redirect them to home
  // which will redirect them to their first project.
  const shouldDirectUserToProject =
    isAuthenticated &&
    !isLoadingUser &&
    onboardingZeroProjectOnlyPaths.some((path) => pathname.startsWith(path)) &&
    userHasAccessToAnyProject;
  if (shouldDirectUserToProject) {
    return <Navigate to={routes.shops.default} replace />;
  }

  // Note (Noah, 2025-09-26): If we're not in an onboarding route, but the user has zero projects,
  // when we need to redirect them back to onboarding. This is e.g. if the user somehow accesses
  // dashboard.replo.app/home after dropping out of the onboarding flow. We redirect to a different
  // part of the flow depending on what info they have filled out on their user object
  const shouldDirectUserToOnboarding =
    isAuthenticated &&
    !isLoadingUser &&
    !isOnboardingPath(pathname) &&
    !userHasAccessToAnyProject;
  if (shouldDirectUserToOnboarding) {
    const doesUserHaveName = user?.firstName || user?.lastName;

    const onboardingPathToRedirectTo = doesUserHaveName
      ? routes.onboarding.shopInfo
      : routes.onboarding.userInfo;

    const redirectUrl = addReferralToUrl(
      `${onboardingPathToRedirectTo}?${searchParams.toString()}`,
      referral,
    );

    return <Navigate to={redirectUrl} replace />;
  }

  return children;
}

function RootRouteIndexRedirectRoute() {
  const templateId = useTemplateIdFromSearchParams();
  const referral = useReferralFromSearchParams();
  const { data: workspaceData, isLoading: isLoadingWorkspaces } =
    workspaceRouter.getUserWorkspacesList.useQuery();

  if (isLoadingWorkspaces) {
    return <FullScreenLoader />;
  }

  if (templateId) {
    return (
      <Navigate
        to={generatePath(routes.home.shareTemplate, { templateId })}
        replace
      />
    );
  }

  const userHasOnlyOneWorkspace =
    (workspaceData?.workspaces ?? []).length === 1;
  const toUrl =
    userHasOnlyOneWorkspace && workspaceData?.workspaces![0]!.id
      ? generatePath("/workspace/:workspaceId/projects", {
          workspaceId: workspaceData.workspaces[0].id,
        })
      : routes.home.root;

  return <Navigate to={addReferralToUrl(toUrl, referral)} replace />;
}

function PartnerProgramRedirectRoute() {
  const { user } = useCurrentUser();

  return (
    <Navigate
      to={
        user!.workspace?.id
          ? generatePath("/workspace/:workspaceId/partner/program", {
              workspaceId: user!.workspace.id,
            })
          : routes.home.root
      }
      replace
    />
  );
}

function ActivePartnershipRequiredRoute({
  children,
}: {
  children: JSX.Element;
}) {
  const { workspace, isLoading: isLoadingWorkspace } =
    useCurrentWorkspaceContext();
  const { projectId } = useParams<{ projectId: string }>();

  if (!isLoadingWorkspace && !workspace?.isOnboarded) {
    const redirectUrl = location.pathname.includes("/shops/")
      ? generatePath(
          "/shops/:projectId/settings/workspace/affiliates/program",
          {
            projectId: projectId ?? "",
          },
        )
      : generatePath("/workspace/:workspaceId/settings/affiliates/program", {
          workspaceId: workspace?.id ?? "",
        });

    return (
      <Navigate
        to={
          workspace?.id
            ? redirectUrl
            : // NOTE (Fran 2023-11-21): Fallback to home if user doesn't have a workspace
              routes.home.root
        }
        replace
      />
    );
  }

  return children;
}

function WorkspaceIndexRedirectRoute() {
  const workspaceId = useCurrentWorkspaceId();
  return (
    <Navigate
      to={
        workspaceId
          ? generatePath(routes.workspace.projects, {
              workspaceId,
            })
          : routes.home.root
      }
      replace
    />
  );
}

function useTemplateIdFromSearchParams() {
  const [searchParams] = useSearchParams();
  return searchParams.get("templateId");
}

function useReferralFromSearchParams() {
  const [searchParams] = useSearchParams();
  return searchParams.get("referral");
}

function ProjectRouteWrapper({
  navigateToDefaultElementAfterFetch = true,
}: {
  navigateToDefaultElementAfterFetch?: boolean;
}) {
  const { projectId } = useParams<{ projectId: string }>();

  if (!projectId) {
    return <Navigate to={routes.home.root} replace />;
  }

  return (
    <CurrentProjectProvider
      projectId={projectId}
      navigateToDefaultElementAfterFetch={navigateToDefaultElementAfterFetch}
    >
      <ProjectContentWithRedirect projectId={projectId} />
    </CurrentProjectProvider>
  );
}

/**
 * Component that handles the project content and marketing seed redirects
 */
function ProjectContentWithRedirect({ projectId }: { projectId: string }) {
  const { project, isLoading } = useCurrentProjectContext();
  const { pathname } = useLocation();

  if (isLoading) {
    return null;
  }

  const marketingSeedData = localStorage.getItem("replo_marketing_seed")
    ? JSON.parse(localStorage.getItem("replo_marketing_seed")!)
    : null;

  if (
    marketingSeedData &&
    project?.hostingType === "replo" &&
    !pathname.includes("/chat") &&
    project.styleLibrary
  ) {
    const currentUrl = new URL(window.location.href);
    const webcontainerParam = currentUrl.searchParams.get("webcontainer");
    const chatUrl = webcontainerParam
      ? `/shops/${projectId}/chat?webcontainer=${webcontainerParam}`
      : `/shops/${projectId}/chat`;
    return <Navigate to={chatUrl} replace />;
  }

  return (
    <>
      {/* TODO (Gabe 2025-06-18): This thing will rerender on log output which is not great. */}
      <SandboxStateProvider>
        <NextDevServerProvider>
          <CurrentChatProvider>
            <InitialProjectSetupModal projectId={projectId} />
            <Outlet />
          </CurrentChatProvider>
        </NextDevServerProvider>
      </SandboxStateProvider>
    </>
  );
}

/**
 * This modal is used to check for full project onboarding status and show modals as needed
 */
function InitialProjectSetupModal({ projectId }: { projectId: string }) {
  const { shouldShowStyleLibraryModal } = useIsProjectSetup();

  if (shouldShowStyleLibraryModal) {
    return <ShopDesignLibrary projectId={projectId} />;
  }

  return null;
}
